import * as React from 'react'
import {
  Box,
  Button,
  FieldLabel,
  FieldValue,
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
  Stack,
  TextAreaInput,
  InputCharLimit,
  Text,
  TextInput,
  ErrorMessage,
} from '@lightspeed/design-system-react'
import { useT } from '@transifex/react'
import { useRegisterManager } from '../../../../../../ng-compat/hooks'
import { STEPS } from '../types'
import { StepBreadcrumbs } from '../StepBreadcrumbs'
import { ServiceItemData } from '../../../../../../models/sale.constants'
import {
  SALE_NOTE_MAX_CHAR,
  THRESHOLD_SALE_NOTE_MAX_CHAR,
} from '../../constants'

interface Props {
  onClose: (shouldCreate?: boolean) => void
  onStepChange: (step: STEPS) => void
  onNoteChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
  onServiceItemChange: (serviceItem: ServiceItemData) => void
  note: string
  serviceItem?: ServiceItemData
}

export const StepWorkDescription: React.FC<Props> = ({
  onClose,
  onStepChange,
  onNoteChange,
  onServiceItemChange,
  note,
  serviceItem,
}) => {
  const registerManager = useRegisterManager()
  const register = registerManager.getActiveRegister()
  const isNoteVisibleToCustomer = register.print_note_on_receipt
  const [itemLocation, setItemLocation] = React.useState<string>(
    serviceItem?.location || ''
  )
  const ITEM_LOCATION_MAX_CHAR = 255
  const hasExceededNoteInputLimit = note.length > SALE_NOTE_MAX_CHAR
  const hasExceededLocationInputLimit =
    itemLocation.length > ITEM_LOCATION_MAX_CHAR
  const shouldDisableNextButton =
    hasExceededLocationInputLimit || hasExceededNoteInputLimit
  const handleNext = () => {
    const updatedServiceItem = {
      ...serviceItem,
      itemDetails: {
        ...serviceItem?.itemDetails,
      },
      location: itemLocation.trim(),
    }
    onServiceItemChange(updatedServiceItem)
    onStepChange(STEPS.SCHEDULE)
  }

  const t = useT()

  const workDescriptionCopies = {
    header: t('Add a work description and location of items', {
      _context: 'Header for work description step in service modal.',
    }),
    headerDescription: t(
      'This will help the staff know what work to do and where to find the items',
      {
        _context: 'Header detail for work description step in service modal.',
      }
    ),
    note: t('Note', {
      _context: 'Input label for work description step in service modal.',
    }),
    staffNote: t('Staff note', {
      _context: 'Input label for work description step in service modal.',
    }),
    noteDescriptionVisible: t(
      'Customers will be able to see this note on the receipt',
      {
        _context: 'Text for work description step in service modal.',
      }
    ),
    noteDescriptionNotVisible: t(
      'Customers will not be able to see this note on the receipt',
      {
        _context: 'Text for work description step in service modal.',
      }
    ),
    location: t('Location of items', {
      _context: 'Input label for work description step in service modal.',
    }),
    includeCustomerItem: t(`Include customer's item`, {
      _context: 'Input label for work description step in service modal.',
    }),
    locationPlaceholder: t(`Enter where in the store items are located`, {
      _context: 'Input placeholder for work description step in service modal.',
    }),
    error: t(
      `Location must be less than ${ITEM_LOCATION_MAX_CHAR} characters`,
      {
        _context: 'Error message for work description step in service modal.',
      }
    ),
    back: t(`Back`, {
      _context: 'Back button text for work description step in service modal.',
    }),
    next: t('Next', {
      _context: 'Next button text for work description step in service modal.',
    }),
  }

  return (
    <Modal onClose={() => onClose()}>
      <StepBreadcrumbs step={STEPS.WORK_DESCRIPTION} />
      <ModalHeader>
        {workDescriptionCopies.header}
        <Text tag="p" className="vd-mt2">
          {workDescriptionCopies.headerDescription}
        </Text>
      </ModalHeader>
      <ModalContent>
        <Stack space={5}>
          <Stack space={0}>
            <FieldLabel
              label={
                isNoteVisibleToCustomer
                  ? workDescriptionCopies.note
                  : workDescriptionCopies.staffNote
              }
              supplementaryLabel={
                isNoteVisibleToCustomer
                  ? workDescriptionCopies.noteDescriptionVisible
                  : workDescriptionCopies.noteDescriptionNotVisible
              }
              htmlFor="note"
            />
            <FieldValue>
              <TextAreaInput
                id="note"
                name="note"
                defaultValue={note}
                rows={5}
                onChange={onNoteChange}
                hasError={hasExceededNoteInputLimit}
              />
              <InputCharLimit
                value={note}
                limit={SALE_NOTE_MAX_CHAR}
                threshold={THRESHOLD_SALE_NOTE_MAX_CHAR}
              />
            </FieldValue>
          </Stack>
          <Stack space={0}>
            <FieldLabel
              label={workDescriptionCopies.location}
              htmlFor="location"
              supplementaryLabel={workDescriptionCopies.includeCustomerItem}
            />
            <FieldValue>
              <TextInput
                placeholder={workDescriptionCopies.locationPlaceholder}
                id="location"
                name="location"
                value={itemLocation}
                onChange={event => setItemLocation(event.target.value)}
              />
              {hasExceededLocationInputLimit && (
                <ErrorMessage>{workDescriptionCopies.error}</ErrorMessage>
              )}
            </FieldValue>
          </Stack>
        </Stack>
      </ModalContent>
      <ModalActions>
        <Box className="vd-btn-group">
          <Button
            variant="supplementary"
            onClick={() => onStepChange(STEPS.CUSTOMER_ITEM)}
            data-cy="button-back"
          >
            {workDescriptionCopies.back}
          </Button>
          <Button
            variant="go"
            disabled={shouldDisableNextButton}
            onClick={handleNext}
            data-cy="button-create-service"
          >
            {workDescriptionCopies.next}
          </Button>
        </Box>
      </ModalActions>
    </Modal>
  )
}

StepWorkDescription.displayName = 'StepWorkDescription'
